import { useRef, KeyboardEvent } from "react";

/**
 * WCAG 2.1 SC 2.1.1 — ARIA-compliant roving tabindex for custom tablists.
 *
 * Usage:
 *   const { tabRefs, getTabProps } = useTablist({ items, activeId, onActivate });
 *
 *   <div role="tablist" aria-label="…">
 *     {items.map((item, i) => (
 *       <button key={item.id} {...getTabProps(item.id, i)}>
 *         {item.label}
 *       </button>
 *     ))}
 *   </div>
 *
 * Keyboard contract (ARIA Authoring Practices Guide §3.21):
 *   ArrowRight / ArrowDown  — focus next tab (wraps)
 *   ArrowLeft  / ArrowUp   — focus previous tab (wraps)
 *   Home                   — focus first tab
 *   End                    — focus last tab
 *   Enter / Space          — activate focused tab (handled natively by <button>)
 */

interface UseTablistOptions<T extends string> {
  items: { id: T }[];
  activeId: T;
  onActivate: (id: T) => void;
}

interface TabProps {
  role: "tab";
  "aria-selected": boolean;
  tabIndex: 0 | -1;
  ref: (el: HTMLButtonElement | null) => void;
  onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => void;
  onClick: () => void;
}

export function useTablist<T extends string>({
  items,
  activeId,
  onActivate,
}: UseTablistOptions<T>) {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  function handleKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    let nextIndex: number | null = null;
    const last = items.length - 1;

    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        nextIndex = index === last ? 0 : index + 1;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        nextIndex = index === 0 ? last : index - 1;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = last;
        break;
    }

    if (nextIndex !== null) {
      e.preventDefault();
      onActivate(items[nextIndex].id);
      tabRefs.current[nextIndex]?.focus();
    }
  }

  function getTabProps(id: T, index: number): TabProps {
    return {
      role: "tab",
      "aria-selected": id === activeId,
      tabIndex: id === activeId ? 0 : -1,
      ref: (el) => {
        tabRefs.current[index] = el;
      },
      onKeyDown: (e) => handleKeyDown(e, index),
      onClick: () => onActivate(id),
    };
  }

  return { tabRefs, getTabProps };
}
